import { useCallback, useMemo } from "react";

import { ACCESS_API_KEYS_LIMIT } from "../constants/accessCatalog.constants";
import {
  apiKeysService,
  type AllowedIp,
  type ApiKeyEntity,
  type CreateApiKeyRequest,
  type CreateApiKeyResponse,
} from "../services/apiKeysService";
import { principalRolesService } from "../services/principalRolesService";
import { usePrincipalRoles, type PrincipalRolesConfig, type RolesByPrincipal } from "./usePrincipalRoles";

/** Map of API key id → its currently assigned roles. */
export type RolesByApiKey = RolesByPrincipal;

// Module-scoped so the reference is stable — usePrincipalRoles keys its callbacks on this object (see its
// PrincipalRolesConfig doc for why an inline object would loop the provider's mount effect).
const API_KEYS_CONFIG: PrincipalRolesConfig<ApiKeyEntity> = {
  // Deliberate cap, not full pagination — each key costs a per-principal roles call. `total` lets the section
  // disclose when the backend holds more keys than the page shows (see ACCESS_API_KEYS_LIMIT).
  //
  // The query's filter fields are ignored on purpose: `GET /api-keys` accepts nothing but `skip`/`limit`.
  // This section never calls `loadWith`, so the query stays at the initial page for the whole session and
  // the behaviour is byte-for-byte what it was before the users listing gained filters.
  fetchPage: ({ skip, limit }) => apiKeysService.getApiKeys(skip, limit),
  listRoles: (keyId) => principalRolesService.listApiKeyRoles(keyId),
  assignRoles: (keyId, roleIds) => principalRolesService.assignApiKeyRoles(keyId, roleIds),
  removeRole: (keyId, roleId) => principalRolesService.removeApiKeyRole(keyId, roleId),
  initialLimit: ACCESS_API_KEYS_LIMIT,
};

/**
 * Single hook for the unified API Keys section. Owns the whole surface:
 *
 * - **Authorization** (the shared `usePrincipalRoles` state machine bound to the API-key endpoints): each key's
 *   assigned roles (`GET /api-keys/:id/roles`, fetched in parallel) plus assign/remove with auto-save.
 *   Effective access is resolved client-side deny-first.
 * - **Lifecycle** (reusing `apiKeysService`): create (+ one-time secret), enable/disable, allowed-IP
 *   management, current-IP detection.
 *
 * Creating a key still requires scopes (the backend mandates them and homologates them into base roles on
 * creation), so after a create/enable/disable we reload to pick up the key's fresh roles. Settings has no
 * Redux slice — all HTTP stays here.
 */
export function useAccessApiKeys() {
  const principal = usePrincipalRoles(API_KEYS_CONFIG);
  const { patchItem } = principal;

  // ---- Lifecycle (key CRUD) ----

  const create = useCallback(
    (body: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => apiKeysService.createApiKey(body),
    [],
  );

  const disable = useCallback((id: string) => apiKeysService.disableApiKey(id), []);
  const enable = useCallback((id: string) => apiKeysService.enableApiKey(id), []);

  const updateAllowedIps = useCallback(
    async (id: string, ips: AllowedIp[]) => {
      const updated = await apiKeysService.updateAllowedIps(id, ips);
      patchItem(id, (key) => ({ ...key, allowed_ips: updated.allowed_ips }));
      return updated;
    },
    [patchItem],
  );

  const getCurrentIp = useCallback(() => apiKeysService.getCurrentIp(), []);

  // Renamed projection of the shared hook (`items` → `keys`, …) plus the lifecycle actions, memoized so the
  // value forwarded by AccessDataProvider stays stable across renders.
  return useMemo(
    () => ({
      keys: principal.items,
      total: principal.total,
      rolesByKey: principal.rolesById,
      rolesUnavailable: principal.rolesUnavailable,
      isLoading: principal.isLoading,
      hasNoAccess: principal.hasNoAccess,
      load: principal.load,
      /**
       * Re-issue the listing with a different page. Settings never calls it — its section keeps the capped
       * first page — but `/app/access/api-keys` needs to raise the limit, since a silent cap on a page whose
       * whole subject is the key catalog would under-report it.
       */
      loadWith: principal.loadWith,
      assignRoles: principal.assignRoles,
      removeRole: principal.removeRole,
      create,
      disable,
      enable,
      updateAllowedIps,
      getCurrentIp,
    }),
    [principal, create, disable, enable, updateAllowedIps, getCurrentIp],
  );
}
